import React, { useEffect, useState } from "react";
import axios from "axios";
import { Upload, Trash2, Image, Check, X } from "lucide-react";

const AdminHeroImageManager = () => {
  const [file, setFile] = useState(null);
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const fetchImages = async () => {
    try {
      const res = await axios.get(
        "https://archpoint.onrender.com/api/hero-images"
      );
      setImages(res.data);
    } catch (error) {
      console.error("Error fetching images:", error);
    }
  };

  useEffect(() => {
    fetchImages();
  }, []);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    if (!selectedFile) {
      setFile(null);
      setPreview(null);
      return;
    }

    if (!selectedFile.type.startsWith("image/")) {
      alert("Please select a valid image file.");
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      alert("Image size must be less than 5MB.");
      return;
    }

    setFile(selectedFile);

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      alert("Please select an image first!");
      return;
    }

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append("file", file); // ✅ FIXED: was "image", now "file"

      const response = await axios.post(
        "https://archpoint.onrender.com/api/hero-images/architectural-upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data", // ✅ ADDED: explicit header
          },
        }
      );

      console.log("Upload success:", response.data);
      alert("Image uploaded successfully!");

      setFile(null);
      setPreview(null);
      document.getElementById("hero-image-input").value = "";

      fetchImages();
    } catch (error) {
      console.error("Upload failed:", error);
      console.log("Server Error:", error.response?.data);
      alert(
        error.response?.data?.message || "Failed to upload image."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this image?"
    );

    if (!confirmDelete) return;

    try {
      await axios.delete(
        `https://archpoint.onrender.com/api/hero-images/${id}`
      );
      alert("Image deleted successfully!");
      fetchImages();
    } catch (error) {
      console.error("Delete failed:", error);
      alert(
        error.response?.data?.message || "Failed to delete image."
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50">
      {/* HEADER */}
      <div className="bg-white border-b border-amber-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-md">
              <Image className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Architectural Design Manager
              </h1>
              <p className="text-sm text-gray-600">
                Upload and manage hero images
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* LEFT PANEL */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden sticky top-8">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-600 px-6 py-4">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Upload New Image
                </h2>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Select Image File
                  </label>
                  <input
                    id="hero-image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="w-full px-4 py-3 border-2 border-amber-300 rounded-lg focus:outline-none focus:border-amber-500 focus:ring-2 focus:ring-amber-200 transition-all file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-amber-100 file:text-amber-800 file:font-medium hover:file:bg-amber-200 file:cursor-pointer text-sm"
                  />
                </div>

                {preview && (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Preview
                    </label>
                    <div className="relative rounded-lg overflow-hidden border-2 border-amber-300">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-full h-48 object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setFile(null);
                          setPreview(null);
                          document.getElementById("hero-image-input").value = "";
                        }}
                        className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-1.5 rounded-full transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading || !file}
                  className={`w-full flex items-center justify-center gap-2 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 ${
                    loading || !file
                      ? "bg-gray-300 cursor-not-allowed"
                      : "bg-gradient-to-r from-amber-500 to-yellow-600 hover:from-amber-600 hover:to-yellow-700 shadow-md hover:shadow-lg"
                  }`}
                >
                  {loading ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      Upload Image
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* RIGHT PANEL */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-amber-200 overflow-hidden">
              <div className="bg-gradient-to-r from-amber-500 to-yellow-600 px-6 py-4 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-white">
                  Image Gallery
                </h2>
                <span className="bg-white/20 px-3 py-1 rounded-full text-sm text-white font-medium">
                  {images.length} {images.length === 1 ? "Image" : "Images"}
                </span>
              </div>

              <div className="p-6">
                {images.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Image className="w-10 h-10 text-amber-500" />
                    </div>
                    <p className="text-gray-600 font-medium mb-1">
                      No images uploaded yet
                    </p>
                    <p className="text-sm text-gray-500">
                      Upload your first image to get started
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {images.map((img) => (
                      <div
                        key={img._id}
                        className="group relative bg-amber-50 border border-amber-200 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300"
                      >
                        <div className="aspect-video overflow-hidden">
                          <img
                            src={img.imageUrl}
                            alt="Hero"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>

                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-4">
                          <button
                            onClick={() => handleDelete(img._id)}
                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg font-medium shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-all duration-300"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete Image
                          </button>
                        </div>

                        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full text-xs font-medium text-amber-700 shadow-md border border-amber-200">
                          Hero Image
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AdminHeroImageManager;